import { api } from './api-client';

// Contrats de platform-api (chantier 9.3) : apps/platform-api/src/routes/tenants.ts et metrics.ts

export const TENANT_STATUSES = ['ACTIVE', 'TRIAL', 'SUSPENDED', 'CANCELLED'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export const ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'DISPATCHER', 'DRIVER', 'ACCOUNTANT', 'VIEWER'] as const;
export type Role = (typeof ROLES)[number];

export interface TenantListItem {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  country: string;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  createdAt: string;
  usersCount: number;
  vehiclesCount: number;
}

export interface Paginated<T> {
  data: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface ListTenantsParams {
  page: number;
  pageSize: number;
  status?: TenantStatus;
  search?: string;
}

export interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  country: string;
  contactEmail: string | null;
  trialEndsAt: string | null;
  createdAt: string;
  suspendedAt: string | null;
  suspendedById: string | null;
  suspendedReason: string | null;
  /** Admin plateforme auteur de la suspension (null si non suspendu ou admin supprimé). */
  suspendedBy: { email: string } | null;
  metrics: {
    usersByRole: Record<Role, number>;
    vehiclesCount: number;
    activeMissionsCount: number;
    lastActivityAt: string | null;
  };
}

/** Réponse de suspend / reactivate : état du tenant après changement de statut. */
export type TenantState = Pick<
  TenantDetail, 'id' | 'name' | 'slug' | 'status' | 'trialEndsAt' | 'suspendedAt' | 'suspendedById' | 'suspendedReason'
>;

export interface CreateTenantInput {
  name: string;
  slug: string;
  country: string;
  ownerEmail: string;
  ownerFirstName: string;
  ownerLastName: string;
  trialDays?: number;
}

export interface CreateTenantResponse {
  tenant: TenantState & { country: string; contactEmail: string | null; createdAt: string };
  invitation: { id: string; email: string; role: Role; status: string; expiresAt: string };
}

export const tenantKeys = {
  all: ['platform', 'tenants'] as const,
  list: (params: ListTenantsParams) => [...tenantKeys.all, 'list', params] as const,
  detail: (id: string) => [...tenantKeys.all, 'detail', id] as const,
};

export const tenantsApi = {
  list: async (params: ListTenantsParams): Promise<Paginated<TenantListItem>> =>
    (await api.get<Paginated<TenantListItem>>('/tenants', { params })).data,
  get: async (id: string): Promise<TenantDetail> => (await api.get<TenantDetail>(`/tenants/${encodeURIComponent(id)}`)).data,
  create: async (input: CreateTenantInput): Promise<CreateTenantResponse> =>
    (await api.post<CreateTenantResponse>('/tenants', input)).data,
  suspend: async (id: string, reason: string): Promise<TenantState> =>
    (await api.patch<TenantState>(`/tenants/${encodeURIComponent(id)}/suspend`, { reason })).data,
  reactivate: async (id: string): Promise<TenantState> =>
    (await api.patch<TenantState>(`/tenants/${encodeURIComponent(id)}/reactivate`)).data,
};
